import moment from 'moment'

import { pendingSelector } from 'api/selectors'
import { createSelector } from 'reselect'
import { sumCalculator } from 'shared/price-utilities'
import { deliveryAddressSelector } from 'auth/selectors'
import { DELIVERY_COLLECT, DELIVERY_HOME } from 'purchase-flow/constants'
import { isAddressFilled } from 'shared/utils'

import * as c from './constants'

const stateSelector = state => state.subscriptionPurchase

export const locationSelector = createSelector(stateSelector, subscriptionPurchase => subscriptionPurchase.location)

export const locationNameSelector = createSelector(locationSelector, location => location?.place?.name)

export const productsSelector = createSelector(locationSelector, location => {
    let { products, waitListProducts } = location || {}

    products = products || []
    waitListProducts = waitListProducts || []

    return [...products, ...waitListProducts]
})

export const credentialTypeSelector = createSelector(locationSelector, location => location?.credentialType)

export const collectAddressSelector = createSelector(
    stateSelector,
    subscriptionPurchase => subscriptionPurchase.collectAddress
)

export const collectAddressLoadingSelector = createSelector(
    pendingSelector,
    ({ getCollectAddress }) => getCollectAddress === true
)

export const purchaseDataSelector = createSelector(
    stateSelector,
    subscriptionPurchase => subscriptionPurchase.purchaseData
)

export const subscriptionLocationErrorSelector = createSelector(
    pendingSelector,
    ({ subscriptionLocation }) =>
        subscriptionLocation && subscriptionLocation instanceof Error && subscriptionLocation.message
)

export const subscriptionLocationLoadingSelector = createSelector(
    pendingSelector,
    ({ subscriptionLocation }) => subscriptionLocation === true
)

export const subscriptionLocationRatesPendingSelector = createSelector(
    pendingSelector,
    pending => pending[c.LOCATION_RATES_PENDING] || false
)

export const addRateToWaitListPendingSelector = createSelector(
    pendingSelector,
    pending => pending[c.ADD_RATE_TO_WAITLIST_PENDING] || false
)

export const hasPromoPriceSelector = createSelector(stateSelector, state => state.hasPromoPrice || false)

export const locationSummarySelector = createSelector(locationSelector, location => ({
    name: location?.place?.name,
    id: location?.place?.id,
    code: location?.place?.code,
}))

export const deliveryProductsSelector = createSelector(
    locationSelector,
    deliveryAddressSelector,
    collectAddressSelector,
    (location, deliveryAddress, collectAddress) => {
        const { address: locationPlaceAddress } = location?.place
        const address = collectAddress?.address || locationPlaceAddress
        const instructions = collectAddress?.instructions
        const extendedProducts = c.PRODUCTS.map(prd => {
            if (prd.prdctCd === DELIVERY_COLLECT) {
                const addressee = address.addressee ? `${address.addressee},` : ''
                prd.prdctLongDesc = `${addressee} ${address.line1}, ${address.line2 && address.line2.concat(',')} ${
                    address.city
                }, ${address.state}, ${address.zip}`
                prd.prdctExtra = instructions
            }

            if (prd.prdctCd === DELIVERY_HOME) {
                prd.prdctLongDesc = isAddressFilled(deliveryAddress)
                    ? `${deliveryAddress.address1}, ${deliveryAddress.address2 &&
                          deliveryAddress.address2.concat(',')} ${deliveryAddress.city}, ${deliveryAddress.city}, ${
                          deliveryAddress.state
                      }, ${deliveryAddress.country}`
                    : ''
            }
            return prd
        })
        return extendedProducts
    }
)
export const selectedProductsSelector = selected =>
    createSelector(productsSelector, locationSummarySelector, (products, loc) => {
        // prdctCd: '',rateId: '', quantity:''
        const product = products.find(prd => prd.prdctCd === selected.prdctCd)
        const rate = product.ratePlans.find(rate => rate.ratePlanId === selected.rateId)

        return [
            {
                productId: product.prdctCd,
                productName: product.prdctName,
                ratePlanId: rate?.ratePlanId,
                ratePlanName: rate?.name,
                locationId: loc.code,
                locationName: loc.name,
            },
        ]
    })

export const invoiceSelector = createSelector(stateSelector, subscriptionPurchase => subscriptionPurchase.invoice)

export const reviewInvoicesSelector = createSelector(invoiceSelector, invoice => {
    if (invoice?.invoiceItems) {
        const nextMonthInvoiceItems = invoice.invoiceItems.filter(i =>
            moment(i.serviceStartDate).isSameOrAfter(invoice.targetDate, 'D')
        )
        const prorataInvoiceItems = invoice.invoiceItems.filter(i =>
            moment(i.serviceStartDate).isBefore(invoice.targetDate, 'D')
        )

        return {
            prorata: {
                tax: sumCalculator(prorataInvoiceItems, 'taxAmount'),
                items: prorataInvoiceItems,
                amountWithoutTax: sumCalculator(prorataInvoiceItems, 'amountWithoutTax'),
            },
            nextMonth: {
                invoiceItems: nextMonthInvoiceItems,
                tax: sumCalculator(nextMonthInvoiceItems, 'taxAmount'),
                amountWithoutTax: sumCalculator(nextMonthInvoiceItems, 'amountWithoutTax'),
            },
        }
    }

    return null
})

export const termsAndConditionsSelector = createSelector(
    stateSelector,
    subscriptionPurchase => subscriptionPurchase.termsConditions
)

export const termsConditionsLoadingSelector = createSelector(
    pendingSelector,
    ({ termsConditionsLoading }) => termsConditionsLoading === true
)

export const termsConditionsErrorSelector = createSelector(
    pendingSelector,
    ({ termsConditionsLoading }) =>
        termsConditionsLoading && termsConditionsLoading instanceof Error && termsConditionsLoading.message
)

export const subscriptionPurchaseBuyPendingSelector = createSelector(
    pendingSelector,
    ({ subscriptionPurchaseBuy }) => subscriptionPurchaseBuy
)
